import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { calculateShiftTimes } from "@/contexts/utils/shift-times";
import { useJobs } from "@/hooks/useJobs";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useStaffCountByJobId } from "@/hooks/useStaffCountByJobId";
import { sortJobsByPopularity } from "@/utils/jobs";
import type { Job, NurseFullRole, WeekData } from "@/utils/models";
import type { ShiftAvailabilityWindow } from "@/utils/shiftAvailability";
import { buildShiftOptionWindows, getShiftIndexForWindow, type ShiftOptionWindow } from "@/utils/shiftOptionWindows";
import { resolveShiftTimeOptions } from "@/utils/shiftTimeOptions";
import { inverseRolesMap } from "@/utils/utilFunctions";
import { buildShiftCardOptions, isSameWindow, rebaseWindowToDate, resolveDefaultJobId, resolveSelectedCardKey, type ShiftCardOption } from "./addStaffUtils";

type Args = {
	/** The panel's schedule weeks; `weekIndex` and `dayNo` are positions in them. */
	weeks: WeekData[];
	dayNo: number;
	weekIndex: number;
	/** The cell's slot (0 day / 1 night) — seeds the automatic default window and is the slot until a card is picked. */
	initialShiftNo: number;
	rolesToDisplay: NurseFullRole[];
	lockedTimes: { startTime: DateTime; endTime: DateTime } | undefined;
	defaultJobId: string | undefined;
	requestJobId: string | undefined;
	/** False = open on "Pick a shift first" (day-level callers); true = preselect the cell's default window. */
	initialShiftSelected: boolean;
	/** The picked location — the automatic default window follows it. */
	selectedRoom: string;
	onShiftChange: ((shiftType: string) => void) | undefined;
};

/**
 * "auto" follows the cell's default hours (it recalculates when the location or the date changes);
 * "explicit" is a card or custom hours the user picked, which nothing overrides (resolved PM Q1/Q2) — a
 * date change moves those same hours to the new day (`pickedOn` is the day they were picked for).
 */
type ShiftSelection = { source: "auto" } | { source: "explicit"; window: ShiftAvailabilityWindow; pickedOn: DateTime } | null;

/** Job-managed departments pick a job; role-mode departments pick a role. */
export type AddStaffScheduleMode = "job" | "role";

export type AddStaffShift = {
	mode: AddStaffScheduleMode;
	jobs: Job[];
	selectedJobId: string | undefined;
	selectJob: (jobId: string) => void;
	selectedRole: NurseFullRole | undefined;
	selectRole: (role: NurseFullRole) => void;
	cardOptions: ShiftCardOption[];
	/** The picked window, or `null` until a shift is picked. */
	window: ShiftAvailabilityWindow | null;
	/** The card that shows as picked — a preset key, `CUSTOM_CARD_KEY`, or `null`. */
	selectedCardKey: string | null;
	/** Legacy day (0) / night (1) slot the shift lands in. */
	shiftNo: number;
	staffingDate: DateTime;
	isLocked: boolean;
	pickWindow: (window: ShiftAvailabilityWindow) => void;
	/** Drop the picked hours: step 1 back to "no shift picked", step 2 back to "Pick a shift first". */
	clearWindow: () => void;
	/** The cell's own default hours — seeds the custom-hours popover when nothing is picked yet. */
	defaultWindow: ShiftAvailabilityWindow;
};

/** The job/role choice and the cards it derives. Split out so the window logic below stays readable. */
const useScheduleTarget = (rolesToDisplay: NurseFullRole[], defaultJobId: string | undefined, requestJobId: string | undefined) => {
	const enabledJobs = useJobs();
	const staffCountByJobId = useStaffCountByJobId();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const mode: AddStaffScheduleMode = jobManagementEnabled && (enabledJobs.length > 0 || !!requestJobId) ? "job" : "role";
	// "First enabled job" = the first one the job picker shows (most-held first), so the list doesn't open on
	// a job nobody in the department holds just because it sorts first alphabetically.
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>(() =>
		resolveDefaultJobId({
			defaultJobId,
			requestJobId,
			// DEFAULT: no popularity signal (roster not loaded yet, or nobody holds a job) → `useJobs()` order,
			// which is alphabetical — the same fallback JobSelector's own ordering uses. Resolved once, on mount.
			enabledJobs: staffCountByJobId ? sortJobsByPopularity(enabledJobs, staffCountByJobId) : enabledJobs,
		}),
	);
	// DEFAULT: role mode starts on the caller's first displayed role — the same "first one" rule as the
	// default job (resolved PM Q4). `undefined` only when the caller displays no roles.
	const [selectedRole, setSelectedRole] = useState<NurseFullRole | undefined>(rolesToDisplay[0]);
	// A request fixes the job — the chips never render then, but guard the setter too.
	const selectJob = useCallback((jobId: string) => !requestJobId && setSelectedJobId(jobId), [requestJobId]);
	return { mode, jobManagementEnabled, jobs: enabledJobs, selectedJobId, selectJob, selectedRole, selectRole: setSelectedRole };
};

/** What the shift hours are resolved for: the day, the location, and the job or role picked in step 1. */
type WindowScope = {
	weeks: WeekData[];
	weekIndex: number;
	dayNo: number;
	selectedRoom: string;
	roleForTimes: NurseFullRole | undefined;
	/** `undefined` in role mode. */
	jobId: string | undefined;
};

/**
 * The default hours of each of the week's slots (day / night) on this date, for this location. Walks the
 * loaded slots themselves — not `shift_types` — so a department whose config lists more shift types than
 * the week has slots can't index past the data (`calculateShiftTimes` tolerates a missing shift type).
 */
const useSlotWindows = ({ weeks, weekIndex, dayNo, selectedRoom, roleForTimes, jobId }: WindowScope): ShiftOptionWindow[] => {
	const { chosenDepartment } = useChosenDepartment();
	return useMemo(
		() =>
			weeks[weekIndex].weeklyShifts.map((weeklyShift, slot) => {
				const { startTime, endTime } = calculateShiftTimes({
					day: weeklyShift.days[dayNo],
					shiftNo: slot,
					nurseRole: roleForTimes,
					chosenDepartment,
					location: selectedRoom,
					nurseJobId: jobId,
				});
				return { start: startTime, end: endTime, lengthHours: endTime.diff(startTime, "hours").hours };
			}),
		[chosenDepartment, weeks, weekIndex, dayNo, roleForTimes, selectedRoom, jobId],
	);
};

type CardOptionsArgs = { jobId: string | undefined; roleForTimes: NurseFullRole | undefined; staffingDate: DateTime; slotWindows: ShiftOptionWindow[] };

/** The shift cards: the job's / role's configured full shifts, else the department's own slot hours. */
const useCardOptions = ({ jobId, roleForTimes, staffingDate, slotWindows }: CardOptionsArgs): ShiftCardOption[] => {
	const { chosenDepartment } = useChosenDepartment();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	return useMemo(() => {
		const role = roleForTimes ? inverseRolesMap[roleForTimes] : undefined;
		const { fullShiftTimes } = resolveShiftTimeOptions({ department: chosenDepartment, jobManagementEnabled, jobId, role });
		const graceMinutes = chosenDepartment.scheduled_shift_delta_minutes;
		const configured = buildShiftOptionWindows({ fullShiftTimes, staffingDate, dayStart: chosenDepartment.dayStart, graceMinutes });
		// No full shifts configured for this job/role: offer the department's own day and night hours — the
		// same two choices the old Day / Night toggle gave — rather than a lone "Custom" card.
		return buildShiftCardOptions(configured.length ? configured : slotWindows);
	}, [chosenDepartment, jobManagementEnabled, jobId, roleForTimes, staffingDate, slotWindows]);
};

/**
 * The picked window: a request's locked hours, else the user's explicit pick (on the panel's current day),
 * else the automatic default.
 */
const resolveWindow = (
	lockedTimes: ShiftAvailabilityWindow | undefined,
	selection: ShiftSelection,
	defaultWindow: ShiftAvailabilityWindow,
	staffingDate: DateTime,
): ShiftAvailabilityWindow | null => {
	if (lockedTimes) return lockedTimes;
	if (selection?.source === "explicit") return rebaseWindowToDate(selection.window, selection.pickedOn, staffingDate);
	if (selection?.source === "auto") return defaultWindow;
	return null;
};

export const useAddStaffShift = (args: Args): AddStaffShift => {
	const { weeks, dayNo, weekIndex, initialShiftNo, rolesToDisplay, initialShiftSelected, selectedRoom, onShiftChange } = args;
	const lockedTimes = useStableLockedWindow(args.lockedTimes);
	const target = useScheduleTarget(rolesToDisplay, args.defaultJobId, args.requestJobId);
	const [selection, setSelection] = useState<ShiftSelection>(initialShiftSelected ? { source: "auto" } : null);

	const staffingDate = weeks[weekIndex].weeklyShifts[initialShiftNo].days[dayNo].date;
	// DEFAULT: job mode has no role of its own — the shift-first flow has no nurse yet — so the caller's first
	// displayed role stands in for the shift-hours lookup. It's a pre-backfill stand-in for job-managed
	// departments whose job rows aren't populated: `resolveShiftTimeOptions` (and calculateShiftTimes'
	// location rows) try the job's own row first, then this role's key, then the department default. So it
	// only decides the hours where the job has no row and that role's hours differ from the others'.
	// Open question with the author (PR #5067): derive it from the selected job instead.
	const roleForTimes = target.mode === "role" ? target.selectedRole : rolesToDisplay[0];
	const jobId = target.mode === "job" ? target.selectedJobId : undefined;
	const slotWindows = useSlotWindows({ weeks, weekIndex, dayNo, selectedRoom, roleForTimes, jobId });
	const defaultWindow = slotWindows[initialShiftNo];
	const cardOptions = useCardOptions({ jobId, roleForTimes, staffingDate, slotWindows });

	const window = useStableWindow(resolveWindow(lockedTimes, selection, defaultWindow, staffingDate));
	// Locked (request) shifts keep the caller's slot exactly as before; otherwise the picked hours decide it.
	const shiftNo = lockedTimes || !window ? initialShiftNo : getShiftIndexForWindow(window);
	useReportShiftChange(weeks[weekIndex].weeklyShifts[shiftNo]?.type, onShiftChange);

	const pickWindow = useCallback(
		(picked: ShiftAvailabilityWindow) => setSelection({ source: "explicit", window: picked, pickedOn: staffingDate }),
		[staffingDate],
	);
	const clearWindow = useCallback(() => setSelection(null), []);

	return {
		...target,
		cardOptions,
		window,
		selectedCardKey: resolveSelectedCardKey(cardOptions, window),
		shiftNo,
		staffingDate,
		isLocked: !!lockedTimes,
		pickWindow,
		clearWindow,
		defaultWindow,
	};
};

/**
 * Callers build `lockedTimes` inline (a fresh object every render). Hand back the same window object while
 * the hours are unchanged, so the rows, the cost query and the grid don't rebuild on every parent render.
 */
const useStableLockedWindow = (lockedTimes: Args["lockedTimes"]): ShiftAvailabilityWindow | undefined => {
	const stable = useRef<ShiftAvailabilityWindow | undefined>(undefined);
	if (!lockedTimes) {
		stable.current = undefined;
	} else if (!stable.current || !isSameWindow(stable.current, { start: lockedTimes.startTime, end: lockedTimes.endTime })) {
		stable.current = { start: lockedTimes.startTime, end: lockedTimes.endTime };
	}
	return stable.current;
};

/**
 * A rebased pick is a fresh object every render (it is recomputed from the picked day); hand back the same
 * window while the hours are unchanged, so the rows and the cost query don't rebuild on every render.
 */
const useStableWindow = (window: ShiftAvailabilityWindow | null): ShiftAvailabilityWindow | null => {
	const stable = useRef<ShiftAvailabilityWindow | null>(window);
	if (!window) stable.current = null;
	else if (!stable.current || !isSameWindow(stable.current, window)) stable.current = window;
	return stable.current;
};

/**
 * Tells the caller when the shift type changes (a card landing in the other half of the day) — never on mount, and
 * not for a slot the day has no row for.
 */
const useReportShiftChange = (shiftType: string | undefined, onShiftChange: ((shiftType: string) => void) | undefined) => {
	const reported = useRef(shiftType);
	useEffect(() => {
		if (shiftType === undefined || reported.current === shiftType) return;
		reported.current = shiftType;
		onShiftChange?.(shiftType);
	}, [shiftType, onShiftChange]);
};
